'use client';

import { fetchMarketingSceneProductList } from '@/api/marketingPages';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useStore } from '@/providers/app-store-provider';
import type { Store } from '@/store';
import logger from '@/utils/client-logger';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { MarketingSceneProductsBlockConfig } from '../../../types';
import {
  MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT,
  MARKETING_PC_PRODUCT_CONTENT_CLASS_NAME,
  MarketingRankingProductCard,
} from './MarketingRankingProductCard';
import {
  getMarketingRankingProductCardView,
  getStringValue,
  isMarketingFlashSaleSoldOutProduct,
  type MarketingProductItem,
  withS3Url,
} from './marketingAnniversaryUtils';
import { MarketingSwiperArrowButton } from './MarketingSwiperArrowButton';
import {
  resolveMarketingSceneProductsCacheState,
  type MarketingSceneProductsCacheState,
} from '../sceneProductsCache';

const MARKETING_SCENE_PRODUCTS_PC_SPACE_BETWEEN = 16;
const MARKETING_SCENE_PRODUCTS_PC_SLIDES_OFFSET = 20;
const MARKETING_SCENE_PRODUCTS_PC_CARD_WIDTH =
  'clamp(164px, min(calc(20.3125vw - 44px), calc(15.625vw + 16px)), 316px)';
const MARKETING_SCENE_PRODUCTS_PC_CARD_HEIGHT =
  'clamp(190px, min(calc(15.234375vw + 34px), calc(11.71875vw + 79px)), 304px)';
const MARKETING_SCENE_PRODUCTS_PC_SWIPER_SLIDE_STYLE = {
  width: MARKETING_SCENE_PRODUCTS_PC_CARD_WIDTH,
};
const MARKETING_SCENE_PRODUCTS_PC_PAGE_SIZE = 20;
const MARKETING_SCENE_PRODUCTS_WAP_PAGE_SIZE = 20;
const MARKETING_SCENE_PRODUCTS_WAP_DISPLAY_COUNT = 6;
const MARKETING_SCENE_PRODUCTS_PC_SKELETON_COUNT = 6;
const MARKETING_SCENE_PRODUCTS_WAP_SKELETON_COUNT = MARKETING_SCENE_PRODUCTS_WAP_DISPLAY_COUNT;
const MARKETING_SCENE_PRODUCTS_US_LOCALE = 'en';

interface MarketingSceneProductsSectionProps {
  sceneProductsBlock: MarketingSceneProductsBlockConfig;
  marketingPageId: number;
  isPreview: boolean;
  adp?: string;
  isWap: boolean;
  cacheState?: MarketingSceneProductsCacheState;
  onCacheStateChange?: (state: MarketingSceneProductsCacheState) => void;
  onProductExposure?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onProductClick?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onTabClick?: (tabName: string, index: number) => void;
}

interface MarketingSceneProductCardProps {
  product: MarketingProductItem;
  index: number;
  tabName: string;
  isWap: boolean;
  currency: Store['currency'];
  userInfo: Store['userInfo'];
  imageHoverRule?: unknown[];
  onProductExposure?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onProductClick?: (product: MarketingProductItem, index: number, tabName: string) => void;
}

function MarketingSceneProductCard({
  product,
  index,
  tabName,
  isWap,
  currency,
  userInfo,
  imageHoverRule,
  onProductExposure,
  onProductClick,
}: MarketingSceneProductCardProps) {
  const { ref: exposureRef } = useValidExposure({
    callback: () => onProductExposure?.(product, index, tabName),
    ts: product.shop_sku || product.id,
  });
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const locale = useLocale();
  const cardView = getMarketingRankingProductCardView(product, currency, userInfo, imageHoverRule);
  const isSoldOut = isMarketingFlashSaleSoldOutProduct(product);
  const shouldStackWapPrice = isWap && locale.toLowerCase() !== MARKETING_SCENE_PRODUCTS_US_LOCALE;

  return (
    <div ref={exposureRef} className="min-w-0">
      <MarketingRankingProductCard
        {...cardView}
        rank={index + 1}
        rankText=""
        soldText=""
        isLarge={false}
        isWap={isWap}
        showRankingMeta={false}
        contentClassName={
          isWap
            ? 'box-border flex min-h-0 flex-1 flex-col justify-end px-[8px] pb-[8px] text-center'
            : MARKETING_PC_PRODUCT_CONTENT_CLASS_NAME
        }
        priceRowClassName={
          isWap
            ? shouldStackWapPrice
              ? 'mt-[6px] flex flex-col items-center gap-y-[2px] font-br'
              : 'mt-[6px] flex flex-wrap items-baseline justify-center gap-x-[6px] gap-y-[2px] font-br'
            : undefined
        }
        priceTextClassName={
          isWap ? 'shrink-0 font-bb text-[14px] font-bold leading-[17px] text-[#282828]' : undefined
        }
        originalPriceTextClassName={
          isWap
            ? shouldStackWapPrice
              ? 'shrink-0 whitespace-nowrap text-[12px] font-normal leading-[14px] text-[#808080] line-through'
              : 'truncate text-[12px] font-normal leading-[14px] text-[#808080] line-through'
            : undefined
        }
        isSoldOut={isSoldOut}
        soldOutText={tMarketing('flashSale.soldOut')}
        width={isWap ? undefined : MARKETING_SCENE_PRODUCTS_PC_CARD_WIDTH}
        height={isWap ? 'auto' : MARKETING_SCENE_PRODUCTS_PC_CARD_HEIGHT}
        imageAreaHeight={isWap ? undefined : MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT}
        imageLayout={isWap ? undefined : 'ranking-normal'}
        onClick={() => onProductClick?.(product, index, tabName)}
      />
    </div>
  );
}

function MarketingSceneProductSkeleton({ isWap }: { isWap: boolean }) {
  if (isWap) {
    return (
      <div
        className="aspect-square w-full overflow-hidden rounded-[6px] bg-white"
        aria-hidden="true"
      >
        <div className="box-border flex h-[112px] shrink-0 items-center justify-center px-[12px] pt-[12px]">
          <div className="h-[78px] w-full max-w-[156px] animate-pulse rounded-[8px] bg-[#eeeeee]/80" />
        </div>
        <div className="box-border flex min-h-0 flex-1 flex-col items-center justify-end px-[8px] pb-[12px] text-center">
          <div className="h-[14px] w-[72%] animate-pulse rounded-[4px] bg-[#eeeeee]/80" />
          <div className="mt-[8px] h-[17px] w-[48%] animate-pulse rounded-[4px] bg-[#eeeeee]/80" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="box-border flex min-w-0 flex-col overflow-hidden rounded-[8px] bg-white"
      style={{
        width: MARKETING_SCENE_PRODUCTS_PC_CARD_WIDTH,
        height: MARKETING_SCENE_PRODUCTS_PC_CARD_HEIGHT,
      }}
      aria-hidden="true"
    >
      <div
        className="box-border flex shrink-0 items-center justify-center"
        style={{ height: MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT }}
      >
        <div className="h-[48%] w-[80%] animate-pulse rounded-[8px] bg-[#eeeeee]/80" />
      </div>
      <div className="box-border min-h-0 flex-1 px-[12px] pb-[16px] pt-[6px] text-center">
        <div className="mx-auto h-[17px] w-[72%] animate-pulse rounded-[4px] bg-[#eeeeee]/80" />
        <div className="mx-auto mt-[10px] h-[19px] w-[46%] animate-pulse rounded-[4px] bg-[#eeeeee]/80" />
      </div>
    </div>
  );
}

export function MarketingSceneProductsSection({
  sceneProductsBlock,
  marketingPageId,
  isPreview,
  adp,
  isWap,
  cacheState,
  onCacheStateChange,
  onProductExposure,
  onProductClick,
  onTabClick,
}: MarketingSceneProductsSectionProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const swiperRef = useRef<SwiperType | null>(null);
  const sceneCategoryScrollRef = useRef<HTMLDivElement | null>(null);
  const sceneCategoryItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const hasCenteredSceneCategoryRef = useRef(false);
  const loadingSceneIndexesRef = useRef<Set<number>>(new Set());
  const hasHydratedCacheRef = useRef(false);
  const hasSceneProductsCacheRef = useRef(Boolean(cacheState));
  const defaultSceneIndex = useMemo(() => {
    const configuredIndex = Number(sceneProductsBlock.default_scene_index);
    return Number.isFinite(configuredIndex) &&
      configuredIndex >= 0 &&
      configuredIndex < sceneProductsBlock.scenes.length
      ? configuredIndex
      : 0;
  }, [sceneProductsBlock.default_scene_index, sceneProductsBlock.scenes.length]);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(defaultSceneIndex);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingSceneIndex, setLoadingSceneIndex] = useState<number | null>(null);
  const [sceneProductLoaded, setSceneProductLoaded] = useState(() =>
    sceneProductsBlock.scenes.map((scene) => Boolean(scene.products_loaded)),
  );
  const [localSceneProducts, setLocalSceneProducts] = useState(() =>
    sceneProductsBlock.scenes.map((scene) => scene.products),
  );
  const [localSceneHoverImageRules, setLocalSceneHoverImageRules] = useState(() =>
    sceneProductsBlock.scenes.map((scene) => scene.hover_image_rule),
  );
  const selectedSceneIndexRef = useRef(selectedSceneIndex);
  const isExpandedRef = useRef(isExpanded);
  const sceneProductLoadedRef = useRef(sceneProductLoaded);
  const localSceneProductsRef = useRef(localSceneProducts);
  const localSceneHoverImageRulesRef = useRef(localSceneHoverImageRules);
  const sceneProductSwiperIndexesRef = useRef(sceneProductsBlock.scenes.map(() => 0));
  const fallbackProducts = sceneProductsBlock.products;
  const scenes = useMemo(
    () =>
      sceneProductsBlock.scenes
        .map((scene, index) => ({
          ...scene,
          source_index: index,
          products: localSceneProducts[index] || scene.products,
          products_loaded: sceneProductLoaded[index] ?? Boolean(scene.products_loaded),
          hover_image_rule:
            localSceneHoverImageRules[index] ??
            scene.hover_image_rule ??
            sceneProductsBlock.hover_image_rule,
          title: getStringValue(scene.title),
        }))
        .filter((scene) => !scene.products_loaded || scene.products.length > 0),
    [
      localSceneHoverImageRules,
      localSceneProducts,
      sceneProductLoaded,
      sceneProductsBlock.hover_image_rule,
      sceneProductsBlock.scenes,
    ],
  );
  const activeScene =
    scenes.find((scene) => scene.source_index === selectedSceneIndex) || scenes[0];
  const allProducts = useMemo(() => {
    const activeSceneProducts = activeScene?.products as Array<Record<string, unknown>> | undefined;
    const productList =
      activeSceneProducts?.length || activeScene?.product_template
        ? activeSceneProducts || []
        : fallbackProducts;

    return (productList || []).map((product) => product as MarketingProductItem);
  }, [activeScene?.product_template, activeScene?.products, fallbackProducts]);
  const displayCount = isWap ? MARKETING_SCENE_PRODUCTS_WAP_DISPLAY_COUNT : allProducts.length;
  const hasMoreProducts = allProducts.length > displayCount;
  const products = useMemo(
    () => (isExpanded || !hasMoreProducts ? allProducts : allProducts.slice(0, displayCount)),
    [allProducts, displayCount, hasMoreProducts, isExpanded],
  );
  const activeSceneSourceIndex = activeScene?.source_index;
  const isActiveSceneLoading =
    activeSceneSourceIndex !== undefined &&
    (loadingSceneIndex === activeSceneSourceIndex ||
      loadingSceneIndexesRef.current.has(activeSceneSourceIndex)) &&
    !sceneProductLoaded[activeSceneSourceIndex];
  const sceneBackgroundColor = isWap
    ? sceneProductsBlock.wap_background_color || sceneProductsBlock.background_color || '#282828'
    : sceneProductsBlock.pc_background_color || sceneProductsBlock.background_color || '#FFFFFF';
  const sceneProductsBackgroundColor = isWap ? sceneBackgroundColor : '#F4F4F4';

  useEffect(() => {
    selectedSceneIndexRef.current = selectedSceneIndex;
  }, [selectedSceneIndex]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    sceneProductLoadedRef.current = sceneProductLoaded;
  }, [sceneProductLoaded]);

  useEffect(() => {
    localSceneProductsRef.current = localSceneProducts;
  }, [localSceneProducts]);

  useEffect(() => {
    localSceneHoverImageRulesRef.current = localSceneHoverImageRules;
  }, [localSceneHoverImageRules]);

  const updateSceneProductsCache = useCallback(
    (state: MarketingSceneProductsCacheState) => {
      hasSceneProductsCacheRef.current = true;
      onCacheStateChange?.({
        selectedSceneIndex,
        isExpanded,
        sceneProductLoaded,
        localSceneProducts,
        sceneHoverImageRules: localSceneHoverImageRules,
        sceneProductSwiperIndexes: sceneProductSwiperIndexesRef.current,
        ...state,
      });
    },
    [
      isExpanded,
      localSceneHoverImageRules,
      localSceneProducts,
      onCacheStateChange,
      sceneProductLoaded,
      selectedSceneIndex,
    ],
  );

  useEffect(() => {
    setSelectedSceneIndex((current) => {
      const currentScene = scenes.find((scene) => scene.source_index === current);
      if (
        (hasSceneProductsCacheRef.current || cacheState) &&
        current >= 0 &&
        current < sceneProductsBlock.scenes.length &&
        (currentScene || localSceneProductsRef.current[current]?.length)
      ) {
        return current;
      }

      return currentScene ? current : (scenes[0]?.source_index ?? defaultSceneIndex);
    });
  }, [cacheState, defaultSceneIndex, sceneProductsBlock.scenes.length, scenes]);

  useEffect(() => {
    if (hasSceneProductsCacheRef.current || cacheState) {
      return;
    }

    setSelectedSceneIndex(defaultSceneIndex);
    selectedSceneIndexRef.current = defaultSceneIndex;
    const nextSceneProductLoaded = sceneProductsBlock.scenes.map((scene) =>
      Boolean(scene.products_loaded),
    );
    const nextLocalSceneProducts = sceneProductsBlock.scenes.map((scene) => scene.products);
    const nextLocalSceneHoverImageRules = sceneProductsBlock.scenes.map(
      (scene) => scene.hover_image_rule,
    );
    sceneProductLoadedRef.current = nextSceneProductLoaded;
    localSceneProductsRef.current = nextLocalSceneProducts;
    localSceneHoverImageRulesRef.current = nextLocalSceneHoverImageRules;
    sceneProductSwiperIndexesRef.current = sceneProductsBlock.scenes.map(() => 0);
    setSceneProductLoaded(nextSceneProductLoaded);
    setLocalSceneProducts(nextLocalSceneProducts);
    setLocalSceneHoverImageRules(nextLocalSceneHoverImageRules);
  }, [cacheState, defaultSceneIndex, sceneProductsBlock.scenes]);

  useEffect(() => {
    if (hasSceneProductsCacheRef.current || cacheState) {
      return;
    }

    isExpandedRef.current = false;
    setIsExpanded(false);
  }, [cacheState, sceneProductsBlock.display_count, sceneProductsBlock.products]);

  useEffect(() => {
    if (hasHydratedCacheRef.current || !cacheState || scenes.length === 0) {
      return;
    }

    const nextCacheState = resolveMarketingSceneProductsCacheState({
      cacheState,
      sceneCount: sceneProductsBlock.scenes.length,
      visibleSceneSourceIndexes: scenes.map((scene) => scene.source_index),
      fallbackSceneIndex: activeScene?.source_index ?? defaultSceneIndex,
    });

    if (!nextCacheState) {
      return;
    }

    hasHydratedCacheRef.current = true;
    hasSceneProductsCacheRef.current = true;
    const nextSelectedSceneIndex = nextCacheState.selectedSceneIndex ?? defaultSceneIndex;
    const nextIsExpanded = Boolean(nextCacheState.isExpanded);

    selectedSceneIndexRef.current = nextSelectedSceneIndex;
    isExpandedRef.current = nextIsExpanded;
    setSelectedSceneIndex(nextSelectedSceneIndex);
    setIsExpanded(nextIsExpanded);

    if (nextCacheState.sceneProductLoaded) {
      sceneProductLoadedRef.current = nextCacheState.sceneProductLoaded;
      setSceneProductLoaded(nextCacheState.sceneProductLoaded);
    }

    if (nextCacheState.localSceneProducts) {
      localSceneProductsRef.current = nextCacheState.localSceneProducts;
      setLocalSceneProducts(nextCacheState.localSceneProducts);
    }

    if (nextCacheState.sceneHoverImageRules) {
      localSceneHoverImageRulesRef.current = nextCacheState.sceneHoverImageRules;
      setLocalSceneHoverImageRules(nextCacheState.sceneHoverImageRules);
    }

    if (nextCacheState.sceneProductSwiperIndexes) {
      sceneProductSwiperIndexesRef.current = nextCacheState.sceneProductSwiperIndexes;
    }
  }, [
    activeScene?.source_index,
    cacheState,
    defaultSceneIndex,
    sceneProductsBlock.scenes.length,
    scenes,
  ]);

  useEffect(() => {
    if (isWap) {
      return;
    }

    const maxIndex = Math.max(0, products.length - 1);
    const nextIndex = Math.min(
      sceneProductSwiperIndexesRef.current[selectedSceneIndex] ?? 0,
      maxIndex,
    );
    swiperRef.current?.slideTo(nextIndex, 0);
  }, [cacheState, isWap, products.length, selectedSceneIndex]);

  const syncSwiperEdgeState = useCallback((swiper: SwiperType) => {
    setIsBeginning((current) => (current === swiper.isBeginning ? current : swiper.isBeginning));
    setIsEnd((current) => (current === swiper.isEnd ? current : swiper.isEnd));
  }, []);

  const handleSceneProductsSwiperSlideChange = useCallback(
    (swiper: SwiperType) => {
      syncSwiperEdgeState(swiper);

      if (isWap) {
        return;
      }

      const sourceIndex = selectedSceneIndexRef.current;
      const nextSceneProductSwiperIndexes = [...sceneProductSwiperIndexesRef.current];
      nextSceneProductSwiperIndexes[sourceIndex] = swiper.activeIndex;
      sceneProductSwiperIndexesRef.current = nextSceneProductSwiperIndexes;

      if ((cacheState && !hasHydratedCacheRef.current) || !hasSceneProductsCacheRef.current) {
        return;
      }

      updateSceneProductsCache({
        selectedSceneIndex: sourceIndex,
        isExpanded: isExpandedRef.current,
        sceneProductSwiperIndexes: nextSceneProductSwiperIndexes,
      });
    },
    [cacheState, isWap, syncSwiperEdgeState, updateSceneProductsCache],
  );

  const scrollSceneCategoryToCenter = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const container = sceneCategoryScrollRef.current;
      const item = sceneCategoryItemRefs.current[index];

      if (!container || !item) {
        return;
      }

      const containerWidth = container.offsetWidth;
      const containerRect = container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemLeft = container.scrollLeft + itemRect.left - containerRect.left;
      const itemWidth = item.offsetWidth;
      const scrollLeft = itemLeft - (containerWidth - itemWidth) / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior,
      });
    },
    [],
  );

  useEffect(() => {
    if (!isWap) {
      return;
    }

    const selectedVisibleIndex = scenes.findIndex(
      (scene) => scene.source_index === selectedSceneIndex,
    );

    if (selectedVisibleIndex < 0) {
      return;
    }

    const behavior: ScrollBehavior = hasCenteredSceneCategoryRef.current ? 'smooth' : 'auto';
    const frameId = window.requestAnimationFrame(() => {
      scrollSceneCategoryToCenter(selectedVisibleIndex, behavior);
      hasCenteredSceneCategoryRef.current = true;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isWap, scenes, scrollSceneCategoryToCenter, selectedSceneIndex]);

  const loadSceneProducts = useCallback(
    async (scene: (typeof scenes)[number], index: number) => {
      if (
        !scene ||
        !marketingPageId ||
        sceneProductLoaded[index] ||
        loadingSceneIndex === index ||
        loadingSceneIndexesRef.current.has(index)
      ) {
        return;
      }

      loadingSceneIndexesRef.current.add(index);
      setLoadingSceneIndex(index);

      try {
        const response = await fetchMarketingSceneProductList({
          marketingPageId,
          isPreview,
          itemIndex: scene.item_index ?? index,
          page: 1,
          pageSize: isWap
            ? MARKETING_SCENE_PRODUCTS_WAP_PAGE_SIZE
            : MARKETING_SCENE_PRODUCTS_PC_PAGE_SIZE,
          adp,
        });
        const items = response?.items || [];
        const nextLocalSceneProducts = localSceneProductsRef.current.map(
          (products, currentIndex) => (currentIndex === index ? items : products),
        );
        const nextLocalSceneHoverImageRules = localSceneHoverImageRulesRef.current.map(
          (imageRule, currentIndex) =>
            currentIndex === index && Array.isArray(response?.hover_image_rule)
              ? response.hover_image_rule
              : imageRule,
        );
        const nextSceneProductLoaded = sceneProductLoadedRef.current.map((value, currentIndex) =>
          currentIndex === index ? true : value,
        );

        localSceneProductsRef.current = nextLocalSceneProducts;
        localSceneHoverImageRulesRef.current = nextLocalSceneHoverImageRules;
        sceneProductLoadedRef.current = nextSceneProductLoaded;
        setLocalSceneProducts(nextLocalSceneProducts);
        setLocalSceneHoverImageRules(nextLocalSceneHoverImageRules);
        setSceneProductLoaded(nextSceneProductLoaded);
        if (hasSceneProductsCacheRef.current) {
          updateSceneProductsCache({
            selectedSceneIndex: selectedSceneIndexRef.current,
            isExpanded: isExpandedRef.current,
            sceneProductLoaded: nextSceneProductLoaded,
            localSceneProducts: nextLocalSceneProducts,
            sceneHoverImageRules: nextLocalSceneHoverImageRules,
            sceneProductSwiperIndexes: sceneProductSwiperIndexesRef.current,
          });
        }
      } catch (error) {
        logger.error(error, 'Failed to fetch marketing scene products:');
      } finally {
        loadingSceneIndexesRef.current.delete(index);
        setLoadingSceneIndex(null);
      }
    },
    [
      adp,
      isPreview,
      isWap,
      loadingSceneIndex,
      marketingPageId,
      sceneProductLoaded,
      updateSceneProductsCache,
    ],
  );

  const handleSceneSelect = useCallback(
    (scene: (typeof scenes)[number], sourceIndex: number, trackingIndex: number) => {
      selectedSceneIndexRef.current = sourceIndex;
      isExpandedRef.current = false;
      setSelectedSceneIndex(sourceIndex);
      setIsExpanded(false);
      updateSceneProductsCache({
        selectedSceneIndex: sourceIndex,
        isExpanded: false,
        sceneProductSwiperIndexes: sceneProductSwiperIndexesRef.current,
      });
      onTabClick?.(scene.title, trackingIndex);
      void loadSceneProducts(scene, sourceIndex);
    },
    [loadSceneProducts, onTabClick, updateSceneProductsCache],
  );

  useEffect(() => {
    if (!activeScene || sceneProductLoaded[activeScene.source_index]) {
      return;
    }

    void loadSceneProducts(activeScene, activeScene.source_index);
  }, [activeScene, loadSceneProducts, sceneProductLoaded]);

  const handleSceneViewMoreClick = useCallback(() => {
    isExpandedRef.current = true;
    setIsExpanded(true);
    updateSceneProductsCache({
      selectedSceneIndex,
      isExpanded: true,
      sceneProductSwiperIndexes: sceneProductSwiperIndexesRef.current,
    });
  }, [selectedSceneIndex, updateSceneProductsCache]);

  const handleSceneProductClick = useCallback(
    (product: MarketingProductItem, index: number, tabName: string) => {
      updateSceneProductsCache({
        selectedSceneIndex: selectedSceneIndexRef.current,
        isExpanded: isExpandedRef.current,
        sceneProductLoaded: sceneProductLoadedRef.current,
        localSceneProducts: localSceneProductsRef.current,
        sceneProductSwiperIndexes: sceneProductSwiperIndexesRef.current,
      });
      onProductClick?.(product, index, tabName);
    },
    [onProductClick, updateSceneProductsCache],
  );

  if (!sceneProductsBlock.visible || scenes.length === 0) {
    return null;
  }

  const renderCategoryCard = (scene: (typeof scenes)[number], index: number) => {
    const sourceIndex = scene.source_index;
    const isActive = sourceIndex === selectedSceneIndex;
    const image = withS3Url(
      isWap
        ? getStringValue(scene.wap_image) ||
            getStringValue(scene.pc_image) ||
            getStringValue(scene.image)
        : getStringValue(scene.pc_image) ||
            getStringValue(scene.wap_image) ||
            getStringValue(scene.image),
    );
    const cardClassName = isWap
      ? `group relative shrink-0 cursor-pointer overflow-hidden rounded-[6px] border-0 bg-transparent p-0 transition-[width,height,margin,transform] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-purple ${
          isActive ? 'h-[72px] w-[120px]' : 'mt-[7px] h-[58px] w-[98px]'
        }`
      : `group relative cursor-pointer overflow-hidden rounded-[12px] border-0 bg-transparent p-0 transition-transform duration-300 ease-out hover:scale-[1.015] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-purple ${
          isActive
            ? 'h-[220px] w-[367px] flex-none basis-[367px]'
            : 'aspect-[254/152] max-h-[152px] max-w-[254px] min-w-[120px] flex-1 basis-0'
        }`;
    const titleMaskStyle = isActive
      ? {
          height: 48,
          background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
        }
      : {
          height: 40,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)',
        };

    return (
      <button
        key={`${scene.title}-${sourceIndex}`}
        ref={(element) => {
          sceneCategoryItemRefs.current[index] = element;
        }}
        type="button"
        className={cardClassName}
        aria-pressed={isActive}
        onClick={() => handleSceneSelect(scene, sourceIndex, index)}
      >
        {image ? (
          <Image
            src={image}
            alt={getStringValue(scene.alt) || scene.title}
            fill
            sizes={isWap ? '120px' : isActive ? '367px' : '20vw'}
            className={`object-cover transition-transform duration-300 ease-out ${
              isActive ? 'scale-100' : 'scale-[1.03] group-hover:scale-[1.06]'
            }`}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                index % 3 === 0
                  ? 'linear-gradient(135deg, #4b2f20 0%, #d6b88a 100%)'
                  : index % 3 === 1
                    ? 'linear-gradient(135deg, #2c7782 0%, #d7c6a8 100%)'
                    : 'linear-gradient(135deg, #475739 0%, #c8c5b5 100%)',
            }}
          />
        )}
        {!isActive && <span className="pointer-events-none absolute inset-0 bg-black/20" />}
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]"
          style={titleMaskStyle}
        />
        <span
          className={
            isWap
              ? `absolute inset-x-[8px] z-[2] ${isActive ? 'bottom-[10px]' : 'bottom-[6px]'} truncate text-center font-bb text-[12px] font-bold leading-[14px] text-white`
              : `absolute inset-x-[8px] z-[2] ${isActive ? 'bottom-[14px]' : 'bottom-[12px]'} truncate text-center text-white ${
                  isActive
                    ? 'font-bb text-[16px] font-bold leading-[19px]'
                    : 'font-br text-[14px] font-normal leading-[17px]'
                }`
          }
        >
          {scene.title}
        </span>
      </button>
    );
  };

  const renderProductCard = (product: MarketingProductItem, index: number) => {
    const tabName = activeScene?.title || '';

    return (
      <MarketingSceneProductCard
        key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
        product={product}
        index={index}
        tabName={tabName}
        isWap={isWap}
        currency={currency}
        userInfo={userInfo}
        imageHoverRule={activeScene?.hover_image_rule}
        onProductExposure={onProductExposure}
        onProductClick={handleSceneProductClick}
      />
    );
  };
  const sceneViewMoreButtonText = tMarketing('common.viewMore');
  const sectionTitle = sceneProductsBlock.title || '';
  const sectionSubTitle = sceneProductsBlock.sub_title || '';
  const sceneViewMoreButton = (
    <button
      type="button"
      className="mx-auto mt-[18px] flex h-[38px] w-[264px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-white bg-transparent font-bb text-[14px] font-bold leading-[18px] text-white disabled:cursor-default disabled:opacity-60"
      onClick={handleSceneViewMoreClick}
    >
      {sceneViewMoreButtonText}
    </button>
  );

  return (
    <section
      className={isWap ? 'relative z-10 my-[20px]' : 'relative z-10 my-[20px] w-full'}
      style={{ backgroundColor: sceneBackgroundColor }}
    >
      <div
        className={
          isWap
            ? 'pb-[20px] pt-[20px]'
            : 'mx-auto w-[calc(100%_-_96px)] max-w-[1680px] py-[40px] min-[1281px]:w-[calc(100%_-_240px)]'
        }
      >
        <div className={isWap ? 'mb-[16px] text-center' : 'mb-[24px] text-center'}>
          <h2
            className={`font-bb font-bold ${isWap ? 'text-white' : 'text-[#282828]'} ${
              isWap ? 'text-[20px] leading-[24px]' : 'text-[32px] leading-[38px]'
            }`}
          >
            {sectionTitle}
          </h2>
          {sectionSubTitle && (
            <p
              className={`mt-[4px] font-br font-normal ${isWap ? 'text-white opacity-60' : 'text-[#808080]'} ${
                isWap ? 'text-[12px] leading-[14px]' : 'text-[14px] leading-[17px]'
              }`}
            >
              {sectionSubTitle}
            </p>
          )}
        </div>

        <div
          ref={isWap ? sceneCategoryScrollRef : undefined}
          className={
            isWap
              ? 'scrollbar-hide flex h-[72px] items-start gap-[6px] overflow-x-auto overscroll-x-contain px-[8px] [-webkit-overflow-scrolling:touch]'
              : 'flex h-[220px] w-full items-center gap-[8px] overflow-hidden'
          }
        >
          {scenes.map(renderCategoryCard)}
        </div>

        {isWap ? (
          <>
            <div
              className="mt-[12px] grid gap-[6px] px-[8px]"
              aria-busy={isActiveSceneLoading}
              style={{
                gridTemplateColumns: 'repeat(2, minmax(min(176px, calc((100% - 6px) / 2)), 1fr))',
              }}
            >
              {isActiveSceneLoading
                ? Array.from({ length: MARKETING_SCENE_PRODUCTS_WAP_SKELETON_COUNT }).map(
                    (_, index) => (
                      <MarketingSceneProductSkeleton
                        key={`scene-wap-skeleton-${activeSceneSourceIndex}-${index}`}
                        isWap
                      />
                    ),
                  )
                : products.map((product, index) => renderProductCard(product, index))}
            </div>
            {!isActiveSceneLoading && hasMoreProducts && !isExpanded && sceneViewMoreButton}
          </>
        ) : (
          <div
            className="relative mt-[8px] overflow-hidden rounded-[12px] py-[24px]"
            style={{ backgroundColor: sceneProductsBackgroundColor }}
            aria-busy={isActiveSceneLoading}
          >
            <div className="group relative">
              <Swiper
                modules={[Navigation]}
                slidesPerView="auto"
                slidesPerGroup={1}
                spaceBetween={MARKETING_SCENE_PRODUCTS_PC_SPACE_BETWEEN}
                slidesOffsetBefore={MARKETING_SCENE_PRODUCTS_PC_SLIDES_OFFSET}
                slidesOffsetAfter={MARKETING_SCENE_PRODUCTS_PC_SLIDES_OFFSET}
                navigation={{
                  nextEl: '.marketing-scene-products-next',
                  prevEl: '.marketing-scene-products-prev',
                }}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  syncSwiperEdgeState(swiper);
                }}
                onSlideChange={handleSceneProductsSwiperSlideChange}
                onProgress={syncSwiperEdgeState}
                onReachBeginning={syncSwiperEdgeState}
                onReachEnd={syncSwiperEdgeState}
                onFromEdge={syncSwiperEdgeState}
                onTransitionEnd={syncSwiperEdgeState}
                className="!overflow-hidden"
              >
                {isActiveSceneLoading
                  ? Array.from({ length: MARKETING_SCENE_PRODUCTS_PC_SKELETON_COUNT }).map(
                      (_, index) => (
                        <SwiperSlide
                          key={`scene-pc-skeleton-${activeSceneSourceIndex}-${index}`}
                          style={MARKETING_SCENE_PRODUCTS_PC_SWIPER_SLIDE_STYLE}
                        >
                          <MarketingSceneProductSkeleton isWap={false} />
                        </SwiperSlide>
                      ),
                    )
                  : products.map((product, index) => (
                      <SwiperSlide
                        key={`${getStringValue(product.id) || getStringValue(product.shop_sku) || index}`}
                        style={MARKETING_SCENE_PRODUCTS_PC_SWIPER_SLIDE_STYLE}
                      >
                        {renderProductCard(product, index)}
                      </SwiperSlide>
                    ))}
              </Swiper>
              <MarketingSwiperArrowButton
                alwaysVisible
                className="marketing-scene-products-prev left-[8px]"
                direction="prev"
                hidden={isActiveSceneLoading || isBeginning}
                label="Previous"
                onClick={() => swiperRef.current?.slidePrev()}
              />
              <MarketingSwiperArrowButton
                alwaysVisible
                className="marketing-scene-products-next right-[8px]"
                direction="next"
                hidden={isActiveSceneLoading || isEnd}
                label="Next"
                onClick={() => swiperRef.current?.slideNext()}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
